import React from 'react';

const MONTHS = new Set(['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']);

function fieldLabel(field) {
  if (!field) return '(ignore)';
  if (MONTHS.has(field)) return field.toUpperCase();
  return field.replace(/_/g, ' ').replace(/\bid\b/i, 'ID');
}

const CONFIDENCE = {
  exact: { label: 'matched', cls: 'bg-green-100 text-green-700' },
  alias: { label: 'matched', cls: 'bg-green-100 text-green-700' },
  manual: { label: 'you set', cls: 'bg-mv-100 text-mv-700' },
  fuzzy: { label: 'best guess — check', cls: 'bg-yellow-100 text-yellow-800' },
};

export default function MappingReview({ plan, onMapColumn, onChangeSheet, onChangeHeaderRow }) {
  const { columns, canonicalFields, sheetNames, sheetName, headerRowIndex, sample, rowCount, droppedRows, unmappedRequired, requiredFields } = plan;

  // A canonical field is "taken" once mapped, so we can hint duplicates.
  const usedFields = columns.filter((c) => c.canonical).map((c) => c.canonical);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-semibold text-sm">Review column mapping</h4>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {sheetNames.length > 1 && (
            <label className="flex items-center gap-1.5 text-gray-500">
              Sheet:
              <select
                value={sheetName}
                onChange={(e) => onChangeSheet(e.target.value)}
                className="border border-gray-300 rounded px-1.5 py-1 text-xs bg-white"
              >
                {sheetNames.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-1.5 text-gray-500">
            Header row:
            <select
              value={headerRowIndex}
              onChange={(e) => onChangeHeaderRow(Number(e.target.value))}
              className="border border-gray-300 rounded px-1.5 py-1 text-xs bg-white"
            >
              {Array.from({ length: 15 }, (_, i) => (
                <option key={i} value={i}>Row {i + 1}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {unmappedRequired.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-xs">
          Map required field{unmappedRequired.length > 1 ? 's' : ''}:{' '}
          {unmappedRequired.map(fieldLabel).join(', ')}. Pick the matching source column below.
        </div>
      )}

      {/* Column mapping */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="pb-2 pr-3 font-medium">Your column</th>
              <th className="pb-2 pr-3 font-medium">MV field</th>
              <th className="pb-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((col) => {
              const conf = col.canonical ? CONFIDENCE[col.confidence] : null;
              const isDuplicate = col.canonical && usedFields.filter((f) => f === col.canonical).length > 1;
              return (
                <tr key={col.index} className="border-t border-gray-100">
                  <td className="py-1.5 pr-3">
                    <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-gray-700">
                      {col.sourceHeader}
                    </code>
                  </td>
                  <td className="py-1.5 pr-3">
                    <select
                      value={col.canonical ?? ''}
                      onChange={(e) => onMapColumn(col.index, e.target.value || null)}
                      className={`border rounded px-1.5 py-1 bg-white ${
                        col.canonical ? 'border-gray-300 text-gray-800' : 'border-gray-200 text-gray-400'
                      }`}
                    >
                      <option value="">(ignore)</option>
                      {canonicalFields.map((f) => (
                        <option key={f} value={f}>
                          {fieldLabel(f)}{requiredFields.includes(f) ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5">
                    {conf && (
                      <span className={`px-1.5 py-0.5 rounded ${conf.cls}`}>{conf.label}</span>
                    )}
                    {isDuplicate && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">duplicate</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Data preview */}
      {sample.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">
            Preview — {rowCount} data row{rowCount === 1 ? '' : 's'}
            {droppedRows > 0 && ` (${droppedRows} blank/subtotal row${droppedRows === 1 ? '' : 's'} removed)`}
          </p>
          <div className="overflow-x-auto border border-gray-100 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {usedFields.filter((f, i) => usedFields.indexOf(f) === i).map((f) => (
                    <th key={f} className="px-2 py-1 text-left font-medium text-gray-500 whitespace-nowrap">
                      {fieldLabel(f)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sample.map((row, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    {usedFields.filter((f, j) => usedFields.indexOf(f) === j).map((f) => (
                      <td key={f} className="px-2 py-1 whitespace-nowrap text-gray-700">
                        {String(row[f] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
